// Exponential backoff with full jitter (05 §4.2). Full jitter, not "±10 %", is
// what stops a fleet of retries re-synchronising into a thundering herd.

export type BackoffPolicy = { baseMs: number; capMs: number; maxAttempts: number }

export const POLICIES = {
  call: { baseMs: 100, capMs: 2_000, maxAttempts: 3 },
  outbox: { baseMs: 5_000, capMs: 30 * 60_000, maxAttempts: 12 },
  inbox: { baseMs: 2_000, capMs: 10 * 60_000, maxAttempts: 8 },
  job: { baseMs: 2 * 60_000, capMs: 2 * 60 * 60_000, maxAttempts: 5 },
  reconnect: { baseMs: 500, capMs: 30_000, maxAttempts: Number.POSITIVE_INFINITY },
} as const satisfies Record<string, BackoffPolicy>

/** `random(0, min(cap, base · 2^attempt))`, attempt counted from 0. */
export function fullJitter(attempt: number, policy: Pick<BackoffPolicy, 'baseMs' | 'capMs'>, random = Math.random): number {
  const ceiling = Math.min(policy.capMs, policy.baseMs * 2 ** Math.max(0, attempt))
  return Math.floor(random() * ceiling)
}

/**
 * When to try next. A server-provided time always wins over the computed
 * delay, plus a little jitter so everybody told "retry at 12:00" does not.
 */
export function nextAttemptAt(
  attempt: number,
  policy: BackoffPolicy,
  opts: { now?: number; retryAt?: number; random?: () => number } = {},
): number | null {
  const now = opts.now ?? Date.now()
  const random = opts.random ?? Math.random
  if (opts.retryAt !== undefined) return Math.max(now, opts.retryAt) + Math.floor(random() * 1_000)
  if (attempt + 1 >= policy.maxAttempts) return null
  return now + fullJitter(attempt, policy, random)
}

/**
 * Retry budget (05 §4.3): retries may be at most `ratio` of first attempts in
 * a rolling window, with a floor. An empty budget means "do not retry now".
 */
export class RetryBudget {
  private calls: number[] = []
  private retries: number[] = []

  constructor(
    private readonly ratio = 0.2,
    private readonly minPerWindow = 5,
    private readonly windowMs = 60_000,
  ) {}

  recordCall(now = Date.now()): void {
    this.calls.push(now)
  }

  /** Spend one retry if the budget allows it. */
  tryRetry(now = Date.now()): boolean {
    const since = now - this.windowMs
    this.calls = this.calls.filter((t) => t > since)
    this.retries = this.retries.filter((t) => t > since)
    const allowed = Math.max(this.minPerWindow, Math.floor(this.calls.length * this.ratio))
    if (this.retries.length >= allowed) return false
    this.retries.push(now)
    return true
  }
}
